import React, { useRef, useEffect } from "react";
import * as THREE from "three";

// Logo colors from the Network Playground geodesic sphere
const LOGO_COLORS = [
  "#E8D5A3", "#F2E6B0", "#D4C890", "#5B9BD5", "#4A8BC2",
  "#6BB3D9", "#D4455A", "#C93A50", "#E85070", "#2A9D8F",
  "#3AAFA9", "#E76F51", "#F4A261", "#D4A0C0", "#C8A0D0",
  "#7B5E7B", "#8B6F8B", "#B8D8BA", "#A8C8AA", "#E8B0B0",
];

export const Logo3D = () => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 6);

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(5, 5, 5);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffd4a0, 0.4);
    dirLight2.position.set(-3, -2, 4);
    scene.add(dirLight2);

    const pointLight = new THREE.PointLight(0x5B9BD5, 0.3, 20);
    pointLight.position.set(0, 3, 3);
    scene.add(pointLight);

    // Main geodesic sphere with colored faces
    const sphereGeo = new THREE.IcosahedronGeometry(1.8, 1);
    const colors = [];
    const faceCount = sphereGeo.attributes.position.count / 3;

    for (let i = 0; i < faceCount; i++) {
      const color = new THREE.Color(LOGO_COLORS[i % LOGO_COLORS.length]);
      colors.push(color.r, color.g, color.b);
      colors.push(color.r, color.g, color.b);
      colors.push(color.r, color.g, color.b);
    }
    sphereGeo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));

    const sphereMat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const sphere = new THREE.Mesh(sphereGeo, sphereMat);
    scene.add(sphere);

    // Wireframe cage (slightly larger)
    const wireGeo = new THREE.IcosahedronGeometry(2.35, 1);
    const wireMat = new THREE.MeshBasicMaterial({
      wireframe: true,
      color: 0xffffff,
      transparent: true,
      opacity: 0.12,
    });
    const wire = new THREE.Mesh(wireGeo, wireMat);
    scene.add(wire);

    // Edge lines for sharper wireframe look
    const edgeGeo = new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(2.35, 1));
    const edgeMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.18,
    });
    const edgeLines = new THREE.LineSegments(edgeGeo, edgeMat);
    scene.add(edgeLines);

    // Animate
    let time = 0;
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      time += 0.008;

      // Smooth floating motion
      const floatY = Math.sin(time * 1.2) * 0.12;
      const floatX = Math.cos(time * 0.8) * 0.06;

      // Sphere rotation
      sphere.rotation.y = time * 0.6;
      sphere.rotation.x = Math.sin(time * 0.4) * 0.15;
      sphere.position.y = floatY;
      sphere.position.x = floatX;

      // Wireframe counter-rotation
      wire.rotation.y = -time * 0.3;
      wire.rotation.z = Math.sin(time * 0.5) * 0.1;
      wire.rotation.x = Math.cos(time * 0.35) * 0.12;
      wire.position.y = floatY;
      wire.position.x = floatX;

      edgeLines.rotation.copy(wire.rotation);
      edgeLines.position.copy(wire.position);

      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (rendererRef.current) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
      sphereGeo.dispose();
      sphereMat.dispose();
      wireGeo.dispose();
      wireMat.dispose();
      edgeGeo.dispose();
      edgeMat.dispose();
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" />;
};
